<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import {
  archiveNotice,
  courses,
  initialize,
  login,
  logout,
  periodTime,
  removeCourse,
  saveCourse,
  store,
  tasks,
  scheduledEvents,
  setTaskDone,
  addTask,
  saveEvent,
  deleteEvent,
  courseToPersonal,
  updatePersonalCourse,
  updates,
  refreshSharedData,
  saveEntryMemo,
} from "./store";
import type { Course, PersonalCourse } from "./store";
import { buildWeekTimetableCourses, groupTotalTimetableSlots } from "./timetable-view";
import { campusDateKey as beijingDate, campusTimeLabel, summarizeFocus } from "./focus-engine.mjs";
import { courseRunsThisWeek, teachingWeekForDate } from "./calendar-view.mjs";
import calendarRules from "../../content/calendar-rules.json";

type Page = "schedule" | "tasks" | "me";
type MakeupRule = {
  weekday: number;
  teachingDate: string;
  extraCourseIds?: string[];
  extraCourseNote?: string;
};
const page = ref<Page>("schedule");
const selectedDate = ref(new Date());
const now = ref(new Date());
const showTotalTimetable = ref(false);
const focusDrawerOpen = ref(false);
const focusMenu = ref<HTMLButtonElement | null>(null);
const focusDrawer = ref<HTMLDivElement | null>(null);
let previousBodyOverflow = "";
const focusTab = ref<"tasks"|"diary"|"total"|"me">("tasks");
const timetableWeek = ref(teachingWeekForDate(beijingDate(new Date()), calendarRules.semesterStart));
const showOffWeek = ref(false);
const courseDialog = ref(false);
const eventDialog = ref(false);
const taskDialog = ref(false);
const detailDialog = ref(false);
const detailSaving = ref(false);
const detailStatus = ref("");
const detailNote = ref("");
const detailBring = ref("");
const detailEntry = ref<{
  kind: "course" | "event" | "task";
  id: string;
  stateId: string;
  title: string;
  when?: string;
  place?: string;
  teacher?: string;
  weeks?: string;
  sharedNotes: string[];
  bring: string[];
  links: Array<{ title: string; url: string }>;
  course?: Course;
  personalEventId?: string;
} | null>(null);
const handbookOpen = ref(false);
const archiveQuery = ref("");
const noticeTitle = ref("");
const noticeText = ref("");
const noticeSource = ref("");
const noticeDate = ref(inputDateTime(new Date().toISOString()));
const loginName = ref("");
const loginKey = ref("");
const passwordChangeOpen = ref(false);
const currentPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const passwordError = ref("");
const passwordNotice = ref("");
const taskTitle = ref("");
const taskDue = ref("");
const editingEventId = ref<string | null>(null);
const eventForm = ref({
  title: "",
  start: "",
  end: "",
  location: "",
  notes: "",
});
const editingCourse = ref<Course | null>(null);
const courseForm = ref({
  name: "",
  weekday: 1,
  periodStart: 1,
  periodEnd: 2,
  location: "",
  teacher: "",
  weekStart: 1,
  weekEnd: 18,
  weekMode: "all" as "all" | "odd" | "even",
  excluded: "",
});
const handbookChapters = ref<
  Array<{
    chapter: string;
    title: string;
    pages: number;
    firstPage: number;
    lastPage: number;
  }>
>([]);
const handbookPages = ref<
  Array<{ id: string; printedPage: number; text: string }>
>([]);
const handbookSelected = ref("");
const handbookLoading = ref(false);
const handbookError = ref("");
const activeNotice = ref("");
const submitting = ref(false);
const timelineScroll = ref<HTMLElement | null>(null);
const followCurrentTime = ref(true);
const timelineDragging = ref(false);
let suppressTimelineClick = false;
const pointerDrag = { id: -1, startY: 0, startTop: 0, moved: false };

const dateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const todayKey = computed(() => dateKey(now.value));
const weekDates = computed(() => {
  const monday = new Date(selectedDate.value);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    return date;
  });
});
const selectedDateKey = computed(() => dateKey(selectedDate.value));
const makeupRule = computed(
  () =>
    (
      calendarRules.makeupDates as Record<string, MakeupRule>
    )[selectedDateKey.value],
);
const isNoClassDay = computed(
  () =>
    (calendarRules.noClassDates as string[]).includes(selectedDateKey.value) &&
    !makeupRule.value,
);
const teachingReferenceDate = computed(
  () => makeupRule.value?.teachingDate || selectedDateKey.value,
);
const academicWeek = computed(() =>
  teachingWeekForDate(teachingReferenceDate.value, calendarRules.semesterStart),
);
const timetableWeekday = computed(
  () => makeupRule.value?.weekday || selectedDate.value.getDay() || 7,
);
const dayCourses = computed(() =>
  isNoClassDay.value
    ? []
    : courses.value
        .filter(
          (course) =>
            (course.weekday === timetableWeekday.value &&
              courseRunsThisWeek(course.weeks || "", academicWeek.value)) ||
            makeupRule.value?.extraCourseIds?.includes(course.id),
        )
        .map((course) => ({
          ...course,
          startMinutes: parseTime(course.time.split("–")[0]),
          endMinutes: parseTime(course.time.split("–")[1]),
        })),
);
const totalTimetableCourses = computed(() =>
  buildWeekTimetableCourses(courses.value, timetableWeek.value, calendarRules, showOffWeek.value)
);
const timetableWeekDates = computed(() => {
  const [y,m,d] = calendarRules.semesterStart.split("-").map(Number);
  const t = Date.UTC(y,m-1,d) + (timetableWeek.value - 1) * 7 * 86400000;
  return Array.from({length:7},(_,i)=>new Date(t+i*86400000).getUTCDate());
});
function showFocusTab(tab: "tasks"|"diary"|"total"|"me" = "tasks") {
  focusTab.value = tab;
  focusDrawerOpen.value = true;
  page.value = tab === "tasks" ? "tasks" : tab === "me" ? "me" : "schedule";
  showTotalTimetable.value = tab === "total";
  if (tab === "total") timetableWeek.value = teachingWeekForDate(beijingDate(now.value), calendarRules.semesterStart);
  if (tab === "diary") chooseDate(new Date());
  void nextTick(() => {
    if (!focusDrawer.value?.contains(document.activeElement)) {
      focusDrawer.value?.querySelector<HTMLButtonElement>("button")?.focus();
    }
  });
}
function closeFocusDrawer() {
  focusDrawerOpen.value = false;
}
watch(focusDrawerOpen, async (open) => {
  if (open) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    await nextTick();
    focusDrawer.value?.querySelector<HTMLButtonElement>("button")?.focus();
  } else {
    document.body.style.overflow = previousBodyOverflow;
    await nextTick();
    focusMenu.value?.focus();
  }
});
function handleFocusDrawerKey(event: KeyboardEvent) {
  if (!focusDrawerOpen.value || detailDialog.value || courseDialog.value || eventDialog.value || taskDialog.value || handbookOpen.value) return;
  if (event.key === "Escape") {
    event.preventDefault();
    closeFocusDrawer();
  } else if (event.key === "Tab") {
    const controls = Array.from(focusDrawer.value?.querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"], summary'
    ) || []).filter((element) => element.getClientRects().length > 0);
    const first = controls[0], last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }
}
function changeWeek(n: number) { timetableWeek.value = Math.max(1, Math.min(18, timetableWeek.value + n)); }
const focusItems = computed(() => {
  const today = beijingDate(now.value), ms = Date.parse(today + "T12:00:00+08:00");
  const classItems = Array.from({length:8},(_,n) => {
    const key = beijingDate(new Date(ms + n*86400000));
    const rule = (calendarRules.makeupDates as Record<string, MakeupRule>)[key];
    if (calendarRules.noClassDates.includes(key) && !rule) return [];
    const w = teachingWeekForDate(rule?.teachingDate || key, calendarRules.semesterStart);
    const weekday = rule?.weekday || (new Date(key+"T12:00:00+08:00").getUTCDay() || 7);
    return courses.value.filter(c => c.weekday === weekday &&
      (courseRunsThisWeek(c.weeks || "",w) || rule?.extraCourseIds?.includes(c.id)))
      .flatMap(c => { const [start,end]=c.time.split("–"); return start&&end
        ? [{ title:c.title, start:key+"T"+start+":00+08:00", end:key+"T"+end+":00+08:00", course:c }]
        : []; });
  }).flat();
  const events = scheduledEvents.value.map(e => ({title:e.title,start:e.scheduledAt,end:e.scheduledEndAt||null,event:e}));
  return [...classItems,...events];
});
const focusResult = computed(() => summarizeFocus({items:focusItems.value,tasks:tasks.value,now:now.value,
  complete:!store.user || store.syncStatus === "ready"}));
const focusTitle = computed(() => store.user && store.syncStatus !== "ready"
  ? "个人安排正在同步" : focusResult.value.focus?.title || "接下来暂时没有安排");
const focusTime = computed(() => focusResult.value.focus ? campusTimeLabel(focusResult.value.focus.start) : "—");
function openFocusItem() {
  const x=focusResult.value.focus;
  if (x?.course) openCourseDetails(x.course);
  else if (x?.event) openEventDetails(x.event);
}
const totalTimetableSlots = computed(() => groupTotalTimetableSlots(totalTimetableCourses.value));
function totalCourseGridStyle(slot: { weekday: number; periodStart: number; periodEnd: number }) {
  return {
    gridColumn: String(slot.weekday + 1),
    gridRow: `${slot.periodStart + 1} / ${slot.periodEnd + 2}`,
  };
}
const timedItems = computed(() => {
  const key = dateKey(selectedDate.value);
  return scheduledEvents.value
    .filter((item) => campusDateKey(item.scheduledAt) === key)
    .map((item) => ({
      ...item,
      id: String(item.id),
      title: String(item.title),
      scheduledAt: item.scheduledAt,
      scheduledEndAt: item.scheduledEndAt,
      summary: item.summary || "",
      startMinutes: parseTime(
        new Date(item.scheduledAt).toLocaleTimeString("zh-CN", {
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
          timeZone: "Asia/Shanghai",
        }),
      ),
      endMinutes:
        parseTime(
          new Date(item.scheduledEndAt || item.scheduledAt).toLocaleTimeString(
            "zh-CN",
            {
              hour: "2-digit",
              minute: "2-digit",
              hourCycle: "h23",
              timeZone: "Asia/Shanghai",
            },
          ),
        ) || 60,
      location: String(item.location || ""),
      kind: "event",
    }));
});
const timelineEntries = computed(() =>
  [
    ...dayCourses.value.map((course) => ({
      id: course.id,
      title: course.title,
      startMinutes: course.startMinutes,
      endMinutes: Math.max(course.endMinutes, course.startMinutes + 45),
      location: course.room || "教室待定",
      detail:
        (makeupRule.value?.extraCourseIds?.includes(course.id)
          ? makeupRule.value.extraCourseNote
          : "") || `${course.period} 节 · ${course.weeks || ""}`,
      kind: "course",
      course,
    })),
    ...timedItems.value.map((item) => ({
      ...item,
      detail: "班级日程",
      kind: "event",
      course: null as Course | null,
    })),
  ].sort((a, b) => a.startMinutes - b.startMinutes),
);
const timelineStart = 7 * 60;
const timelineEnd = 22 * 60;
const timelineStyle = (start: number, end: number) => ({
  top: `${Math.max(0, start - timelineStart)}px`,
  height: `${Math.max(42, Math.min(end, timelineEnd) - Math.max(start, timelineStart))}px`,
});
const clockLineStyle = computed(() => ({
  top: `${Math.max(0, Math.min(timelineEnd - timelineStart, now.value.getHours() * 60 + now.value.getMinutes() - timelineStart))}px`,
}));
const clockLabel = computed(() =>
  new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: "Asia/Shanghai",
  }).format(now.value),
);
const remainingTasks = computed(() =>
  tasks.value
    .filter((item) => !item.done)
    .sort((a, b) =>
      String(a.deadlineAt || "9999").localeCompare(
        String(b.deadlineAt || "9999"),
      ),
    ),
);
const completedTasks = computed(() => tasks.value.filter((item) => item.done));
const filteredNotices = computed(() =>
  store.notices.filter(
    (item) =>
      !archiveQuery.value ||
      `${item.title} ${item.rawText} ${item.source}`
        .toLowerCase()
        .includes(archiveQuery.value.toLowerCase()),
  ),
);
const historicalSummaries = computed(() =>
  updates.filter(
    (item) =>
      !archiveQuery.value ||
      `${item.title} ${item.text} ${item.source || ""}`
        .toLowerCase()
        .includes(archiveQuery.value.toLowerCase()),
  ),
);
const selectedHandbook = computed(
  () =>
    handbookChapters.value.find(
      (doc) => doc.chapter === handbookSelected.value,
    ) || null,
);

let clockTimer: number | undefined;
let sharedRefreshTimer: number | undefined;
let lastSharedRefreshAt = 0;
let timelineResizeObserver: ResizeObserver | undefined;
function refreshWhenVisible() {
  if (
    document.visibilityState === "visible" &&
    Date.now() - lastSharedRefreshAt >= 30_000
  ) {
    lastSharedRefreshAt = Date.now();
    void refreshSharedData();
  }
}
onMounted(() => {
  void initialize();
  clockTimer = window.setInterval(() => {
    now.value = new Date();
    if (followCurrentTime.value && dateKey(selectedDate.value) === dateKey(now.value)) {
      void nextTick(() => centerTimelineOnNow());
    }
  }, 30_000);
  sharedRefreshTimer = window.setInterval(() => {
    lastSharedRefreshAt = Date.now();
    void refreshSharedData();
  }, 120_000);
  document.addEventListener("visibilitychange", refreshWhenVisible);
  window.addEventListener("focus", refreshWhenVisible);
  document.addEventListener("keydown", handleFocusDrawerKey);
  void nextTick(() => {
    if (timelineScroll.value) {
      timelineResizeObserver = new ResizeObserver(() => {
        if (followCurrentTime.value) void nextTick(() => centerTimelineOnNow());
      });
      timelineResizeObserver.observe(timelineScroll.value);
    }
    if (dateKey(selectedDate.value) === dateKey(now.value)) {
      void nextTick(() => centerTimelineOnNow());
    }
  });
});
onUnmounted(() => {
  if (focusDrawerOpen.value) document.body.style.overflow = previousBodyOverflow;
  if (clockTimer) window.clearInterval(clockTimer);
  if (sharedRefreshTimer) window.clearInterval(sharedRefreshTimer);
  document.removeEventListener("visibilitychange", refreshWhenVisible);
  window.removeEventListener("focus", refreshWhenVisible);
  document.removeEventListener("keydown", handleFocusDrawerKey);
  timelineResizeObserver?.disconnect();
});
function chooseDate(date: Date) {
  selectedDate.value = new Date(date);
  const isToday = dateKey(date) === dateKey(now.value);
  followCurrentTime.value = isToday;
  void nextTick(() => {
    if (isToday) centerTimelineOnNow("smooth");
    else timelineScroll.value?.scrollTo({ top: 0, behavior: "auto" });
  });
}
function shiftWeek(amount: number) {
  const next = new Date(selectedDate.value);
  next.setDate(next.getDate() + amount * 7);
  selectedDate.value = next;
}
function setPage(value: Page) {
  showFocusTab(value === "schedule" ? "diary" : value);
  void nextTick(() => window.scrollTo({ top: 0, behavior: "auto" }));
}
function centerTimelineOnNow(behavior: ScrollBehavior = "auto") {
  const element = timelineScroll.value;
  if (!element || dateKey(selectedDate.value) !== dateKey(now.value)) return;
  const currentMinute = now.value.getHours() * 60 + now.value.getMinutes();
  const minuteOffset = currentMinute - timelineStart;
  const target = Math.max(0, Math.min(900, minuteOffset - element.clientHeight / 2));
  element.scrollTo({ top: target, behavior });
}
function pauseFollowingNow() {
  if (dateKey(selectedDate.value) === dateKey(now.value)) followCurrentTime.value = false;
}
function resumeFollowingNow() {
  followCurrentTime.value = true;
  void nextTick(() => centerTimelineOnNow("smooth"));
}
function startTimelinePointer(event: PointerEvent) {
  pauseFollowingNow();
  if (event.pointerType !== "mouse" || event.button !== 0) return;
  const element = timelineScroll.value;
  if (!element) return;
  pointerDrag.id = event.pointerId;
  pointerDrag.startY = event.clientY;
  pointerDrag.startTop = element.scrollTop;
  pointerDrag.moved = false;
  timelineDragging.value = true;
  element.setPointerCapture(event.pointerId);
}
function moveTimelinePointer(event: PointerEvent) {
  const element = timelineScroll.value;
  if (!element || pointerDrag.id !== event.pointerId) return;
  const delta = event.clientY - pointerDrag.startY;
  if (Math.abs(delta) > 3) pointerDrag.moved = true;
  if (pointerDrag.moved) {
    event.preventDefault();
    element.scrollTop = pointerDrag.startTop - delta;
  }
}
function endTimelinePointer(event: PointerEvent) {
  if (pointerDrag.id !== event.pointerId) return;
  suppressTimelineClick = pointerDrag.moved;
  pointerDrag.id = -1;
  timelineDragging.value = false;
  if (suppressTimelineClick) window.setTimeout(() => { suppressTimelineClick = false; }, 0);
}
function preventClickAfterTimelineDrag(event: MouseEvent) {
  if (!suppressTimelineClick) return;
  event.preventDefault();
  event.stopPropagation();
  suppressTimelineClick = false;
}
function hasSomethingOnDate(date: Date) {
  const key = dateKey(date);
  const makeup = (
    calendarRules.makeupDates as Record<
      string,
      { weekday: number; teachingDate: string; extraCourseIds?: string[] }
    >
  )[key];
  const blocked =
    (calendarRules.noClassDates as string[]).includes(key) && !makeup;
  const reference = makeup?.teachingDate || key;
  const week = teachingWeekForDate(reference, calendarRules.semesterStart);
  const weekday = makeup?.weekday || date.getDay() || 7;
  return (
      (!blocked &&
        (courses.value.some(
          (course) =>
            course.weekday === weekday &&
            courseRunsThisWeek(course.weeks || "", week),
        ) ||
          makeup?.extraCourseIds?.some((id) =>
            courses.value.some((course) => course.id === id),
          ))) ||
    scheduledEvents.value.some(
      (item) => campusDateKey(item.scheduledAt) === key,
    ) ||
    tasks.value.some(
      (item) =>
        item.deadlineAt && campusDateKey(String(item.deadlineAt)) === key,
    )
  );
}
function detailState(actionId: string) {
  return store.data.actionStates.find((saved) => saved.actionId === actionId);
}
function openDetail(entry: NonNullable<typeof detailEntry.value>) {
  detailEntry.value = entry;
  const saved = detailState(entry.stateId);
  detailNote.value = String(saved?.note || "");
  const blocks = Array.isArray(saved?.blocks) ? saved.blocks : [];
  detailBring.value = blocks
    .filter((block): block is Record<string, unknown> => Boolean(block) && typeof block === "object")
    .filter((block) => block.type === "bring")
    .map((block) => String(block.text || ""))
    .filter(Boolean)
    .join("、");
  detailStatus.value = "";
  detailDialog.value = true;
}
function openCourseDetails(course: Course) {
  const related = updates.filter(
    (item) =>
      (Array.isArray(item.relatedIds) && item.relatedIds.includes(course.id)) ||
      (Array.isArray(course.relatedIds) && course.relatedIds.includes(item.id)),
  );
  const courseDay = `周${["一", "二", "三", "四", "五", "六", "日"][course.weekday - 1] || "?"} · ${course.period} 节 · ${course.time}`;
  openDetail({
    kind: "course",
    id: course.id,
    stateId: `course:${course.id}`,
    title: course.title,
    when: [courseDay, course.weeks].filter(Boolean).join(" · "),
    place: course.room || "教室待定",
    teacher: course.teacher || "",
    sharedNotes: related.map((item) => `${item.title}${item.text ? `：${item.text}` : ""}`),
    bring: [],
    links: [],
    course,
  });
}
function openEventDetails(item: Record<string, any>) {
  const isPersonal = String(item.id).startsWith("personal:");
  const when = item.scheduledAt ? dueLabel(String(item.scheduledAt)) : "";
  const summary = [item.summary, item.instructions]
    .map((value) => String(value || "").trim())
    .filter((value, index, values) => value && values.indexOf(value) === index);
  openDetail({
    kind: "event",
    id: String(item.id),
    stateId: String(item.id),
    title: String(item.title),
    when,
    place: String(item.location || ""),
    sharedNotes: summary,
    bring: Array.isArray(item.bring) ? item.bring.map(String) : [],
    links: Array.isArray(item.links)
      ? item.links.map((link: Record<string, any>) => ({ title: String(link.title || link.label || "查看详情"), url: String(link.url || "") })).filter((link: {url: string}) => link.url)
      : [],
    personalEventId: isPersonal ? String(item.id).slice("personal:".length) : undefined,
  });
}
function openTaskDetails(item: Record<string, any>) {
  const summary = [item.summary, item.instructions]
    .map((value) => String(value || "").trim())
    .filter((value, index, values) => value && values.indexOf(value) === index);
  openDetail({
    kind: "task",
    id: String(item.id),
    stateId: String(item.id),
    title: String(item.title),
    when: item.deadlineAt ? `截止 · ${dueLabel(String(item.deadlineAt))}` : "",
    place: String(item.location || ""),
    sharedNotes: summary,
    bring: Array.isArray(item.bring) ? item.bring.map(String) : [],
    links: Array.isArray(item.links)
      ? item.links.map((link: Record<string, any>) => ({ title: String(link.title || link.label || "查看详情"), url: String(link.url || "") })).filter((link: {url: string}) => link.url)
      : [],
  });
}
async function submitDetailMemo() {
  if (!detailEntry.value) return;
  if (!store.user) {
    detailStatus.value = "登录后可保存个人备忘和携带清单。";
    return;
  }
  detailSaving.value = true;
  const ok = await saveEntryMemo(detailEntry.value.stateId, detailNote.value, detailBring.value);
  detailSaving.value = false;
  detailStatus.value = ok ? "个人备忘已保存" : store.error || "保存失败，请重试。";
}
function editDetailEntry() {
  const entry = detailEntry.value;
  if (!entry) return;
  detailDialog.value = false;
  if (entry.kind === "course" && entry.course) openCourse(entry.course);
  else if (entry.kind === "event" && entry.personalEventId)
    openEvent({ id: `personal:${entry.personalEventId}`, title: entry.title });
}
function openTimelineEntry(item: {
  id: string;
  kind: string;
  course: Course | null;
  title: string;
  scheduledAt?: string;
  scheduledEndAt?: string | null;
  location?: string;
  summary?: string;
  [key: string]: any;
}) {
  if (item.kind === "course" && item.course) openCourseDetails(item.course);
  else if (item.kind === "event") openEventDetails(item);
}
function openCourse(course?: Course) {
  if (!store.user) {
    setPage("me");
    store.error = "登录后即可编辑个人课表；班级基准课表不会被改动。";
    return;
  }
  editingCourse.value = course || null;
  store.error = "";
  if (course) {
    const personal = courseToPersonal(course).slots[0];
    courseForm.value = {
      name: course.title,
      weekday: course.weekday,
      periodStart: personal.periodStart,
      periodEnd: personal.periodEnd,
      location: course.room || "",
      teacher: course.teacher || "",
      weekStart: personal.weeks.start,
      weekEnd: personal.weeks.end,
      weekMode: personal.weeks.mode,
      excluded: personal.weeks.exclude.join("、"),
    };
  } else {
    courseForm.value = {
      name: "",
      weekday: selectedDate.value.getDay() || 7,
      periodStart: 1,
      periodEnd: 2,
      location: "",
      teacher: "",
      weekStart: academicWeek.value,
      weekEnd: 18,
      weekMode: "all",
      excluded: "",
    };
  }
  courseDialog.value = true;
}
function openEvent(item?: {
  id: string;
  title: string;
  scheduledAt?: string;
  scheduledEndAt?: string | null;
  location?: string;
  summary?: string;
}) {
  if (!store.user) {
    setPage("me");
    store.error = "登录后即可添加个人日程。";
    return;
  }
  const personalId = item?.id.startsWith("personal:")
    ? item.id.slice("personal:".length)
    : null;
  const existing = personalId
    ? store.data.items.find((entry) => entry.id === personalId)
    : null;
  store.error = "";
  editingEventId.value = existing?.id || null;
  if (existing?.start) {
    eventForm.value = {
      title: existing.title,
      start: inputDateTime(existing.start),
      end: existing.end ? inputDateTime(existing.end) : "",
      location: existing.location || "",
      notes: existing.notes || "",
    };
  } else {
    const start = new Date(selectedDate.value);
    start.setHours(Math.max(7, now.value.getHours() + 1), 0, 0, 0);
    eventForm.value = {
      title: "",
      start: inputDateTime(start.toISOString()),
      end: "",
      location: "",
      notes: "",
    };
  }
  eventDialog.value = true;
}
async function submitEvent() {
  if (!eventForm.value.title.trim() || !eventForm.value.start) return;
  if (
    eventForm.value.end &&
    new Date(eventForm.value.end).getTime() <=
      new Date(eventForm.value.start).getTime()
  ) {
    store.error = "结束时间必须晚于开始时间。";
    return;
  }
  submitting.value = true;
  const ok = await saveEvent(
    eventForm.value.title,
    eventForm.value.start,
    eventForm.value.end,
    eventForm.value.location,
    eventForm.value.notes,
    editingEventId.value || undefined,
  );
  submitting.value = false;
  if (ok) eventDialog.value = false;
}
async function removeEvent() {
  if (!editingEventId.value) return;
  if (!window.confirm("删除这条个人日程？")) return;
  submitting.value = true;
  const ok = await deleteEvent(editingEventId.value);
  submitting.value = false;
  if (ok) eventDialog.value = false;
}
async function submitCourse() {
  const start = Number(courseForm.value.periodStart);
  const end = Number(courseForm.value.periodEnd);
  if (!courseForm.value.name.trim() || start < 1 || end > 11 || start > end)
    return;
  if (
    !Number.isInteger(courseForm.value.weekStart) ||
    !Number.isInteger(courseForm.value.weekEnd) ||
    courseForm.value.weekStart < 1 ||
    courseForm.value.weekEnd > 30 ||
    courseForm.value.weekStart > courseForm.value.weekEnd
  ) {
    store.error = "请填写有效的起止周次。";
    return;
  }
  const excluded = Array.from(
    new Set(
      (courseForm.value.excluded.match(/\d+/g) || [])
        .map(Number)
        .filter(
          (week) =>
            week >= courseForm.value.weekStart &&
            week <= courseForm.value.weekEnd,
        ),
    ),
  );
  const result: PersonalCourse = {
    name: courseForm.value.name.trim(),
    teacher: courseForm.value.teacher.trim() || null,
    section: null,
    slots: [
      {
        weekday: Number(courseForm.value.weekday),
        periodStart: start,
        periodEnd: end,
        weeks: {
          start: Number(courseForm.value.weekStart),
          end: Number(courseForm.value.weekEnd),
          mode: courseForm.value.weekMode,
          exclude: excluded,
        },
        location: courseForm.value.location.trim() || null,
      },
    ],
  };
  submitting.value = true;
  try {
    const ok = editingCourse.value?.id.startsWith("mine:")
      ? await updatePersonalCourse(editingCourse.value.id, result)
      : await saveCourse(
          result,
          editingCourse.value?.id.startsWith("edit:")
            ? editingCourse.value.id.slice(5)
            : editingCourse.value?.id,
        );
    if (ok) courseDialog.value = false;
    else if (!store.error) store.error = "课程未保存，请重新打开课程后再试。";
  } catch {
    store.error = "课程保存遇到错误，请重试。";
  } finally {
    submitting.value = false;
  }
}
async function deleteCourse() {
  const restoringSharedCourse = editingCourse.value?.id.startsWith("edit:");
  if (
    !editingCourse.value ||
    !window.confirm(
      restoringSharedCourse
        ? "撤销个人修改，恢复班级原课程？"
        : "从你的个人课表移除此课程？班级基准不会改变。",
    )
  )
    return;
  if (await removeCourse(editingCourse.value)) courseDialog.value = false;
}
async function submitTask() {
  if (!taskTitle.value.trim()) return;
  if (!store.user) {
    taskDialog.value = false;
    setPage("me");
    store.error = "登录后即可创建并同步个人待办。";
    return;
  }
  submitting.value = true;
  const ok = await addTask(taskTitle.value, taskDue.value);
  submitting.value = false;
  if (ok) {
    taskTitle.value = "";
    taskDue.value = "";
    taskDialog.value = false;
  }
}
async function submitLogin(event: SubmitEvent) {
  const form = event.currentTarget as HTMLFormElement | null;
  const identity =
    form?.querySelector<HTMLInputElement>('input[autocomplete="username"]')?.value ??
    loginName.value;
  const key =
    form?.querySelector<HTMLInputElement>('input[type="password"]')?.value ??
    loginKey.value;
  if (await login(identity, key)) loginKey.value = "";
}
async function submitLogout() {
  await logout();
  passwordChangeOpen.value = false;
  currentPassword.value = "";
  newPassword.value = "";
  confirmPassword.value = "";
  passwordError.value = "";
  passwordNotice.value = "";
}
async function submitPasswordChange() {
  passwordError.value = "";
  passwordNotice.value = "";
  const current = currentPassword.value.replace(/[\s\u200B-\u200D\uFEFF]/g, "");
  const next = newPassword.value.replace(/[\s\u200B-\u200D\uFEFF]/g, "");
  const confirm = confirmPassword.value.replace(/[\s\u200B-\u200D\uFEFF]/g, "");
  if (next.length < 8 || next.length > 128) {
    passwordError.value = "新密码需要是 8–128 位。";
    return;
  }
  if (next !== confirm) {
    passwordError.value = "两次输入的新密码不一致。";
    return;
  }
  if (current === next) {
    passwordError.value = "新密码需要与当前密码不同。";
    return;
  }

  store.busy = true;
  try {
    const response = await fetch("/api/auth/key", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ currentKey: current, newKey: next }),
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.ok) {
      if (result?.error === "current_key_invalid") throw new Error("当前密码不正确。");
      if (result?.error === "unauthorized") throw new Error("登录状态已失效，请重新登录。");
      if (result?.error === "storage_unavailable") throw new Error("账号服务暂不可用，请稍后重试。");
      throw new Error("修改密码失败，请检查输入后重试。");
    }
    currentPassword.value = "";
    newPassword.value = "";
    confirmPassword.value = "";
    passwordChangeOpen.value = false;
    passwordNotice.value = "密码已修改；下次请使用新密码登录。";
  } catch (error) {
    passwordError.value = error instanceof Error ? error.message : "修改密码失败，请稍后重试。";
  } finally {
    store.busy = false;
  }
}
async function submitNotice() {
  if (!noticeTitle.value.trim() || !noticeText.value.trim()) return;
  submitting.value = true;
  const ok = await archiveNotice(
    noticeTitle.value,
    noticeText.value,
    noticeSource.value,
    noticeDate.value,
  );
  submitting.value = false;
  if (ok) {
    noticeTitle.value = "";
    noticeText.value = "";
    noticeSource.value = "";
    activeNotice.value = "";
  }
}
async function toggleDone(id: string, done: boolean) {
  await setTaskDone(id, done);
}
function dueLabel(value: string | null) {
  if (!value) return "未设截止日";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Shanghai",
  }).format(new Date(value));
}
function parseTime(value: string | undefined) {
  if (!value) return 0;
  const [hour, minute] = value.split(":").map(Number);
  return (hour || 0) * 60 + (minute || 0);
}
function campusDateKey(value: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const read = (type: string) =>
    parts.find((part) => part.type === type)?.value || "00";
  return `${read("year")}-${read("month")}-${read("day")}`;
}
function inputDateTime(value: string) {
  const date = new Date(value);
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}
async function openHandbook() {
  if (!store.user) {
    store.error = "登录后可查看学生手册。";
    return;
  }
  handbookOpen.value = true;
  if (handbookChapters.value.length) return;
  try {
    handbookLoading.value = true;
    const response = await fetch("/api/library/handbook", {
      credentials: "same-origin",
      cache: "no-store",
    });
    const result = await response.json();
    if (!response.ok || !Array.isArray(result.chapters))
      throw new Error("学生手册暂时无法载入。");
    handbookChapters.value = result.chapters;
    if (handbookChapters.value[0])
      await selectHandbookChapter(handbookChapters.value[0].chapter);
  } catch (error) {
    handbookError.value =
      error instanceof Error ? error.message : "学生手册暂时无法载入。";
  } finally {
    handbookLoading.value = false;
  }
}
async function selectHandbookChapter(chapter: string) {
  handbookSelected.value = chapter;
  handbookError.value = "";
  handbookLoading.value = true;
  try {
    const response = await fetch(
      `/api/library/handbook?chapter=${encodeURIComponent(chapter)}`,
      { credentials: "same-origin", cache: "no-store" },
    );
    const result = await response.json();
    if (!response.ok || !Array.isArray(result.pages))
      throw new Error("这章暂时无法载入。");
    handbookPages.value = result.pages;
  } catch (error) {
    handbookError.value =
      error instanceof Error ? error.message : "这章暂时无法载入。";
  } finally {
    handbookLoading.value = false;
  }
}
</script>

<template>
  <div class="app-frame focus-app">
    <button ref="focusMenu" class="focus-menu" type="button" aria-label="展开待办"
      aria-haspopup="dialog" :aria-expanded="focusDrawerOpen" aria-controls="focus-drawer"
      @click="showFocusTab()"><span aria-hidden="true">···</span></button>

    <main class="page-area focus-page">
      <section class="focus-home">
        <div class="focus-orbit" aria-hidden="true"></div>
        <div class="focus-main">
          <span class="focus-caption">NEXT UP · 接下来</span>
          <button class="focus-activity" :disabled="!focusResult.focus" @click="openFocusItem">
            <strong>{{ focusTime }}</strong><span>{{ focusTitle }}</span>
          </button>
          <p v-if="store.user && store.syncStatus !== 'ready'" class="focus-summary">
            个人课表尚未完整同步，暂不推荐下一件事。
          </p>
          <p v-else class="focus-summary">
            今天还剩 {{ focusResult.remainingEvents }} 项日程，{{ focusResult.incompleteTasks }} 项待办未完成。
            <small v-if="!store.user">仅公开安排 · 登录后载入个人课程</small>
          </p>
        </div>
      </section>
      <div v-if="focusDrawerOpen" class="focus-shade" @click="closeFocusDrawer"></div>
      <div v-if="focusDrawerOpen" id="focus-drawer" ref="focusDrawer" class="focus-drawer"
        role="dialog" aria-modal="true" aria-labelledby="focus-drawer-title"
        :class="{wide:focusTab==='total'}">
        <header class="focus-drawer-title"><div><strong id="focus-drawer-title">{{ focusTab==='tasks'?'待办':focusTab==='diary'?'日程':focusTab==='total'?'总课表':'我的' }}</strong>
          <small>其他功能</small></div><button aria-label="关闭" @click="closeFocusDrawer">✕</button></header>
        <nav class="focus-tabs" aria-label="附加功能">
          <button :class="{active:focusTab==='tasks'}" @click="showFocusTab('tasks')">待办</button>
          <button :class="{active:focusTab==='diary'}" @click="showFocusTab('diary')">日程</button>
          <button :class="{active:focusTab==='total'}" @click="showFocusTab('total')">总课表</button>
          <button :class="{active:focusTab==='me'}" @click="showFocusTab('me')">我的</button>
        </nav>
      <section
        v-if="page === 'schedule'"
        class="schedule-page"
        :class="{ 'is-total-timetable': showTotalTimetable }"
      >
        <div v-if="!showTotalTimetable" class="date-rail" aria-label="选择日期">
          <button class="date-tool" aria-label="上一周" @click="shiftWeek(-1)">‹</button>
          <button
            v-for="date in weekDates"
            :key="dateKey(date)"
            class="date-chip"
            :class="{
              selected: dateKey(date) === dateKey(selectedDate),
              today: dateKey(date) === todayKey,
            }"
            @click="chooseDate(date)"
          >
            <span>{{
              ["日", "一", "二", "三", "四", "五", "六"][date.getDay()]
            }}</span
            ><strong>{{ date.getDate() }}</strong
            ><i v-if="hasSomethingOnDate(date)"></i>
          </button>
          <button class="date-tool" aria-label="下一周" @click="shiftWeek(1)">›</button>
          <button class="date-tool" aria-label="添加个人日程" @click="openEvent()">＋</button>
          <button class="date-tool" aria-label="打开总课表" @click="showFocusTab('total')">▦</button>
        </div>
        <div v-else class="focus-weekcontrols">
          <button class="date-tool" aria-label="上一周" @click="changeWeek(-1)">‹</button>
          <label>第 <select v-model.number="timetableWeek" aria-label="选择教学周">
            <option v-for="week in 18" :key="week" :value="week">{{week}}</option>
          </select> 周 · {{ timetableWeek % 2 ? '单周' : '双周' }}</label>
          <button class="date-tool" aria-label="下一周" @click="changeWeek(1)">›</button>
          <button class="date-tool" @click="timetableWeek = teachingWeekForDate(beijingDate(now),calendarRules.semesterStart)">本周</button>
          <label class="focus-offweek"><input type="checkbox" v-model="showOffWeek" />非本周课</label>
          <button class="date-tool" @click="openCourse()">＋ 添加课程</button>
        </div>

        <section class="timeline-shell" :aria-label="showTotalTimetable ? '本周总课表' : '当天时间轴'">
          <div
            v-if="!showTotalTimetable"
            ref="timelineScroll"
            class="timeline-scroll"
            :class="{ 'is-dragging': timelineDragging }"
            @wheel="pauseFollowingNow"
            @touchstart.passive="pauseFollowingNow"
            @pointerdown="startTimelinePointer"
            @pointermove="moveTimelinePointer"
            @pointerup="endTimelinePointer"
            @pointercancel="endTimelinePointer"
            @click.capture="preventClickAfterTimelineDrag"
          >
            <div class="timeline-track">
              <div
                v-for="hour in 16"
                :key="hour"
                class="hour-mark"
                :style="{ top: `${(hour - 1) * 60}px` }"
              >
                <span>{{ String(6 + hour).padStart(2, "0") }}:00</span>
              </div>
              <article
                v-for="item in timelineEntries"
                :key="item.id"
                class="time-entry"
                :class="[
                  `entry-${item.kind}`,
                  {
                    personal:
                      item.id.startsWith('mine:') ||
                      item.id.startsWith('personal:'),
                  },
                ]"
                :style="timelineStyle(item.startMinutes, item.endMinutes)"
                :tabindex="
                  item.kind === 'course' || item.id.startsWith('personal:')
                    ? 0
                    : undefined
                "
                :role="
                  item.kind === 'course' || item.id.startsWith('personal:')
                    ? 'button'
                    : undefined
                "
                @click="openTimelineEntry(item)"
                @keydown.enter.prevent="openTimelineEntry(item)"
              >
                <time>{{
                  item.startMinutes
                    ? `${String(Math.floor(item.startMinutes / 60)).padStart(2, "0")}:${String(item.startMinutes % 60).padStart(2, "0")}`
                    : ""
                }}</time>
                <div class="entry-copy">
                  <strong>{{ item.title }}</strong
                  ><span
                    >{{ item.location
                    }}<template v-if="item.detail">
                      · {{ item.detail }}</template
                    ></span
                  >
                </div>
                <span class="entry-kind">{{
                  item.kind === "course" ? "课程" : "日程"
                }}</span>
              </article>
              <div
                v-if="dateKey(selectedDate) === todayKey"
                class="current-time"
                :style="clockLineStyle"
              >
                <span>{{ clockLabel }}</span>
              </div>
            </div>
          <div v-if="!timelineEntries.length" class="timeline-empty">
              <span class="empty-sun">◌</span><strong>今天暂时空着</strong>
            </div>
          </div>
          <div v-else class="total-timetable-scroll" aria-label="按星期排列的课程总表">
            <p v-if="!store.user" class="timetable-login-note">
              当前显示班级公共课程；登录后会合并你的个人分班与选修课程。
            </p>
            <div v-if="totalTimetableCourses.length" class="total-timetable-grid">
              <div class="total-timetable-corner">节次</div>
              <div
                v-for="day in 7"
                :key="`head-${day}`"
                class="total-timetable-day-head"
              >
                周{{ ["一", "二", "三", "四", "五", "六", "日"][day - 1] }} <small>{{ timetableWeekDates[day-1] }}</small>
              </div>
              <template v-for="period in 11" :key="`period-${period}`">
                <div
                  class="total-timetable-period"
                  :style="{ gridColumn: 1, gridRow: period + 1 }"
                >
                  <strong>第{{ period }}节</strong>
                  <small>{{ periodTime(period, period).split("–")[0] }}</small>
                </div>
                <div
                  v-for="day in 7"
                  :key="`${day}-${period}`"
                  class="total-timetable-cell"
                  :style="{ gridColumn: day + 1, gridRow: period + 1 }"
                ></div>
              </template>
              <div
                v-for="slot in totalTimetableSlots"
                :key="`${slot.weekday}-${slot.periodStart}-${slot.periodEnd}`"
                class="total-timetable-slot"
                :style="totalCourseGridStyle(slot)"
              >
                <button
                  v-for="course in slot.courses"
                  :key="course.id"
                  type="button"
                  class="total-timetable-course"
                  :class="{ personal: /^(mine:|personal:|edit:)/.test(course.id), inactive: course.inactive }"
                  @click="openCourseDetails(course)"
                >
                  <strong>{{ course.title }}</strong>
                  <span>
                    {{ course.time }}<template v-if="course.room"> · {{ course.room }}</template>
                  </span>
                  <small>{{ course.period }} 节 · {{ course.weeks }}</small>
                </button>
              </div>
            </div>
            <div v-else class="total-timetable-empty">本教学周没有排课。</div>
          </div>
          <button
            v-if="!showTotalTimetable && dateKey(selectedDate) === todayKey && !followCurrentTime"
            class="follow-now"
            type="button"
            @click="resumeFollowingNow"
          >回到现在</button>
        </section>
      </section>

      <section v-else-if="page === 'tasks'" class="tasks-page">
        <header class="section-title">
          <div>
            <span class="eyebrow">YOUR NEXT STEPS</span>
            <h1>待办</h1>
            <p>从通知和日常计划里，整理出要完成的事。</p>
          </div>
          <button class="primary-action" @click="taskDialog = true">
            ＋ 新建
          </button>
        </header>
        <div v-if="!store.user" class="login-hint">
          <span>◌</span>
          <div>
            <strong>登录后跨设备保存</strong>
            <p>不登录也能查看班级公开事项。</p>
          </div>
          <button @click="showFocusTab('me')">去登录</button>
        </div>
        <div v-if="remainingTasks.length" class="task-group">
          <div class="group-caption">
            待完成 <span>{{ remainingTasks.length }}</span>
          </div>
          <article
            v-for="item in remainingTasks"
            :key="item.id"
            class="task-item"
          >
            <button
              class="task-check"
              :aria-label="`完成${item.title}`"
              :disabled="!store.user || store.busy"
              @click.stop="toggleDone(String(item.id), true)"
            ></button>
            <button class="task-main task-detail-trigger" @click="openTaskDetails(item)">
              <strong>{{ item.title }}</strong>
              <p>
                {{
                  item.summary ||
                  item.instructions ||
                  item.location ||
                  "班级提醒事项"
                }}
              </p>
              <div class="task-meta">
                <span>{{ item.origin }}</span
                ><time
                  :class="{
                    urgent:
                      item.deadlineAt &&
                      new Date(item.deadlineAt).getTime() <
                        Date.now() + 86400000,
                  }"
                  >{{ dueLabel(item.deadlineAt) }}</time
                >
              </div>
            </button>
            <a
              v-if="item.links?.[0]?.url"
              class="task-link"
              :href="item.links[0].url"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
              >↗</a
            >
          </article>
        </div>
        <div v-else class="calm-empty">
          <span>✓</span><strong>今天没有待办</strong>
          <p>空出来的时间，刚好留给自己。</p>
        </div>
        <details v-if="completedTasks.length" class="completed-group">
          <summary>
            已经完成 <span>{{ completedTasks.length }}</span>
          </summary>
          <article
            v-for="item in completedTasks"
            :key="item.id"
            class="task-item is-done"
          >
            <button
              class="task-check checked"
              :aria-label="`取消完成${item.title}`"
              @click="toggleDone(String(item.id), false)"
            >
              ✓
            </button>
            <button class="task-main task-detail-trigger" @click="openTaskDetails(item)">
              <strong>{{ item.title }}</strong>
              <div class="task-meta">
                <span>{{ item.origin }}</span
                ><time>{{ dueLabel(item.deadlineAt) }}</time>
              </div>
            </button>
          </article>
        </details>
      </section>

      <section v-else class="me-page">
        <header class="section-title">
          <div>
            <span class="eyebrow">YOUR SPACE</span>
            <h1>我的</h1>
            <p>账号、课程和通知原文。</p>
          </div>
        </header>
        <section class="identity-card">
          <template v-if="store.user"
            ><div class="avatar-mark">{{ store.user.name.slice(0, 1) }}</div>
            <div class="identity-copy">
              <strong>{{ store.user.name }}</strong
              ><span>班级成员 · 数据已同步</span>
            </div>
            <button
              class="outline-action"
              :disabled="store.busy"
              @click="submitLogout"
            >
              退出
            </button></template
          >
          <template v-else
            ><div class="identity-copy login-copy">
              <strong>登录个人空间</strong
              ><span>保存你的课表、待办并提交全班通知原文。</span>
            </div></template
          >
        </section>
        <button
          v-if="store.user"
          type="button"
          class="menu-row"
          @click="passwordChangeOpen = !passwordChangeOpen; passwordError = ''; passwordNotice = ''"
        >
          <span class="menu-icon">密</span
          ><span><strong>修改密码</strong><small>需要验证当前密码</small></span
          ><b>{{ passwordChangeOpen ? "收起" : "›" }}</b>
        </button>
        <form
          v-if="store.user && passwordChangeOpen"
          class="login-form"
          @submit.prevent="submitPasswordChange"
        >
          <label><span>当前密码</span><input v-model="currentPassword" type="password" autocomplete="current-password" required placeholder="输入当前密码" /></label>
          <label><span>新密码</span><input v-model="newPassword" type="password" autocomplete="new-password" required minlength="8" maxlength="128" placeholder="8–128 位" /></label>
          <label><span>确认新密码</span><input v-model="confirmPassword" type="password" autocomplete="new-password" required minlength="8" maxlength="128" placeholder="再次输入新密码" /></label>
          <button class="primary-action wide" :disabled="store.busy || !currentPassword || !newPassword || !confirmPassword">{{ store.busy ? "正在保存…" : "保存新密码" }}</button>
        </form>
        <p v-if="passwordError" class="inline-error">{{ passwordError }}</p>
        <p v-if="passwordNotice" class="inline-success">{{ passwordNotice }}</p>
        <form
          v-if="!store.user"
          class="login-form"
          @submit.prevent="submitLogin"
        >
          <label
            ><span>姓名或 ID</span
            ><input
              v-model="loginName"
              autocomplete="username"
              placeholder="输入姓名或班级 ID" /></label
          ><label
            ><span>登录密码</span
            ><input
              v-model="loginKey"
              type="password"
              autocomplete="off"
              placeholder="输入登录密码" /></label
          ><button
            class="primary-action wide"
            :disabled="store.busy || !loginName.trim() || !loginKey"
          >
            {{ store.busy ? "正在登录…" : "进入个人空间" }}
          </button>
        </form>
        <p v-if="store.error" class="inline-error">{{ store.error }}</p>

        <section class="archive-section">
          <header class="archive-heading">
            <div>
              <span class="eyebrow">ORIGINAL NOTICES</span>
              <h2>通知原文档案</h2>
              <p>全班共享 · 保存原文，方便之后查证。</p>
            </div>
            <span class="archive-count">{{ store.notices.length }}</span>
          </header>
          <p v-if="store.noticeError" class="inline-error">
            {{ store.noticeError }}
          </p>
          <div v-if="store.user" class="archive-compose">
            <button
              class="compose-trigger"
              @click="activeNotice = activeNotice ? '' : 'new'"
            >
              <span>＋</span
              ><span
                ><strong>归档一条通知</strong
                ><small>粘贴原文，补上来源和收到时间</small></span
              ><b>{{ activeNotice === "new" ? "收起" : "添加" }}</b>
            </button>
            <form
              v-if="activeNotice === 'new'"
              class="notice-form"
              @submit.prevent="submitNotice"
            >
              <label
                ><span>标题</span
                ><input
                  v-model="noticeTitle"
                  required
                  maxlength="180"
                  placeholder="给通知起一个便于查找的标题" /></label
              ><label
                ><span>通知原文</span
                ><textarea
                  v-model="noticeText"
                  required
                  maxlength="12000"
                  rows="7"
                  placeholder="保留原文措辞和段落，不要只写摘要"
                ></textarea>
              </label>
              <div class="form-columns">
                <label
                  ><span>来源</span
                  ><input
                    v-model="noticeSource"
                    maxlength="300"
                    placeholder="如：辅导员 / 班群" /></label
                ><label
                  ><span>收到时间</span
                  ><input v-model="noticeDate" type="datetime-local" required
                /></label>
              </div>
              <p class="form-note">
                提交后全班成员都可以查阅。图片原件暂不支持上传，请先粘贴可复制的原文。
              </p>
              <button
                class="primary-action wide"
                :disabled="
                  store.busy ||
                  submitting ||
                  !noticeTitle.trim() ||
                  !noticeText.trim()
                "
              >
                保存原文档案
              </button>
            </form>
          </div>
          <div v-else class="archive-login-note">
            登录后可提交通知原文，已归档内容仍可查看。
          </div>
          <label v-if="store.notices.length" class="archive-search"
            ><span>⌕</span
            ><input v-model="archiveQuery" placeholder="搜索标题、原文或来源"
          /></label>
          <div v-if="filteredNotices.length" class="notice-list">
            <article
              v-for="notice in filteredNotices.slice(0, 30)"
              :key="notice.id"
              class="notice-card"
            >
              <button
                class="notice-summary"
                @click="
                  activeNotice = activeNotice === notice.id ? '' : notice.id
                "
              >
                <span class="notice-date">{{
                  dueLabel(notice.receivedAt)
                }}</span
                ><strong>{{ notice.title }}</strong
                ><small
                  >{{ notice.source || "来源未注明" }} · 由
                  {{ notice.savedBy }} 归档</small
                ><b>{{ activeNotice === notice.id ? "−" : "+" }}</b>
              </button>
              <div v-if="activeNotice === notice.id" class="notice-original">
                <div class="original-label">原文</div>
                <p>{{ notice.rawText }}</p>
              </div>
            </article>
          </div>
          <div v-else-if="!historicalSummaries.length" class="archive-empty">
            <span>▤</span
            ><strong>{{
              archiveQuery ? "没有匹配的原文" : "档案从这里开始"
            }}</strong>
            <p>
              {{
                archiveQuery
                  ? "换一个关键词试试。"
                  : "收到通知时，把原文存下来，班上每个人都能回看。"
              }}
            </p>
          </div>
          <details v-if="historicalSummaries.length" class="history-summaries">
            <summary>
              过往通知整理稿 <span>{{ historicalSummaries.length }}</span>
            </summary>
            <p class="history-note">
              这是旧网站整理的摘要，不能当作通知原文。拿到原件后，请使用上方入口重新归档。
            </p>
            <article
              v-for="item in historicalSummaries"
              :key="item.id"
              class="history-item"
            >
              <div>
                <strong>{{ item.title }}</strong
                ><small
                  >{{ item.date }} · {{ item.source || "来源未记录" }} ·
                  原件待补</small
                >
              </div>
              <p>{{ item.text }}</p>
            </article>
          </details>
        </section>
        <footer class="about-foot">
          <span>947654</span><span>简明 · 共用 · 可追溯</span>
        </footer>
      </section>
      </div>
    </main>

    <nav v-if="false" class="bottom-nav" aria-label="主导航">
      <button
        :class="{ active: page === 'schedule' }"
        @click="setPage('schedule')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3.5" y="5.5" width="17" height="15" rx="3" />
          <path d="M8 3.5v4M16 3.5v4M4 9.5h16" />
          <path d="M8 13h2m4 0h2M8 16.5h2" /></svg
        ><span>日程</span></button
      ><button :class="{ active: page === 'tasks' }" @click="setPage('tasks')">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="m5 7 1.5 1.5L9 6M12 7h7M5 14l1.5 1.5L9 13m3 1h7M5 20l1.5 1.5L9 19m3 1h7"
          /></svg
        ><span>待办</span
        ><i v-if="remainingTasks.length" class="nav-count">{{
          remainingTasks.length
        }}</i></button
      ><button :class="{ active: page === 'me' }" @click="setPage('me')">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20c.5-3.7 2.9-5.5 7-5.5s6.5 1.8 7 5.5" /></svg
        ><span>我的</span>
      </button>
    </nav>
    <div v-if="store.error && page !== 'me'" class="toast-error" role="status">
      {{ store.error }}
    </div>
  </div>

  <div v-if="detailDialog && detailEntry" class="modal-scrim" @click.self="detailDialog = false">
    <section class="sheet-dialog detail-sheet" role="dialog" aria-modal="true" aria-labelledby="detail-title">
      <header class="sheet-head">
        <div>
          <span class="eyebrow">{{ detailEntry.kind === "course" ? "COURSE DETAILS" : detailEntry.kind === "event" ? "SCHEDULE DETAILS" : "TASK DETAILS" }}</span>
          <h2 id="detail-title">{{ detailEntry.title }}</h2>
        </div>
        <button class="close-button" aria-label="关闭" @click="detailDialog = false">×</button>
      </header>
      <div class="detail-facts">
        <p v-if="detailEntry.when"><span>时间</span>{{ detailEntry.when }}</p>
        <p v-if="detailEntry.place"><span>地点</span>{{ detailEntry.place }}</p>
        <p v-if="detailEntry.teacher"><span>教师</span>{{ detailEntry.teacher }}</p>
      </div>
      <section class="detail-section">
        <h3>{{ detailEntry.kind === "event" && detailEntry.id.startsWith("personal:") ? "日程原备注" : "班级备注" }}</h3>
        <p v-if="detailEntry.sharedNotes.length" v-for="(note, index) in detailEntry.sharedNotes" :key="index" class="detail-copy">{{ note }}</p>
        <p v-else class="detail-muted">暂无班级补充说明</p>
      </section>
      <section class="detail-section">
        <h3>应带物品</h3>
        <ul v-if="detailEntry.bring.length" class="detail-bring">
          <li v-for="thing in detailEntry.bring" :key="thing">{{ thing }}</li>
        </ul>
        <p v-else class="detail-muted">暂无指定物品</p>
      </section>
      <nav v-if="detailEntry.links.length" class="detail-links" aria-label="相关链接">
        <a v-for="link in detailEntry.links" :key="link.url" :href="link.url" target="_blank" rel="noopener noreferrer">{{ link.title }} ↗</a>
      </nav>
      <section class="detail-section personal-detail-section">
        <h3>个人备忘</h3>
        <textarea v-model="detailNote" rows="3" maxlength="2000" :disabled="!store.user" placeholder="只对自己可见，例如：下课后去实验室补签"></textarea>
        <label class="detail-personal-bring"><span>我还要带</span><input v-model="detailBring" maxlength="1000" :disabled="!store.user" placeholder="用顿号或逗号分隔，例如：校园卡、实验报告" /></label>
        <p v-if="!store.user" class="detail-muted">登录后可保存仅自己可见的备忘和携带清单。</p>
        <p v-else-if="detailStatus" class="detail-status" role="status">{{ detailStatus }}</p>
      </section>
      <footer class="detail-actions">
        <button v-if="detailEntry.kind === 'course' || detailEntry.personalEventId" class="outline-action" @click="editDetailEntry">编辑我的安排</button>
        <button class="primary-action" :disabled="detailSaving || !store.user" @click="submitDetailMemo">{{ detailSaving ? "保存中…" : "保存个人备注" }}</button>
      </footer>
    </section>
  </div>

  <div
    v-if="courseDialog"
    class="modal-scrim"
    @click.self="courseDialog = false"
  >
    <section
      class="sheet-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="course-title"
    >
      <header class="sheet-head">
        <div>
          <span class="eyebrow">PERSONAL TIMETABLE</span>
          <h2 id="course-title">
            {{ editingCourse ? "编辑个人课表" : "添加课程" }}
          </h2>
        </div>
        <button
          class="close-button"
          aria-label="关闭"
          @click="courseDialog = false"
        >
          ×
        </button>
      </header>
      <p class="sheet-explain">此修改仅对你的账号生效，不会更改班级课表。</p>
      <form class="editor-form" @submit.prevent="submitCourse">
        <label
          ><span>课程名称</span
          ><input
            v-model="courseForm.name"
            required
            maxlength="120"
            placeholder="例如：高等数学"
        /></label>
        <div class="form-columns">
          <label
            ><span>星期</span
            ><select v-model.number="courseForm.weekday">
              <option v-for="day in 7" :key="day" :value="day">
                周{{ ["一", "二", "三", "四", "五", "六", "日"][day - 1] }}
              </option>
            </select></label
          ><label
            ><span>教室</span
            ><input
              v-model="courseForm.location"
              maxlength="80"
              placeholder="例如：A3308"
          /></label>
        </div>
        <div class="form-columns">
          <label
            ><span>开始节次</span
            ><select v-model.number="courseForm.periodStart">
              <option v-for="period in 11" :key="period" :value="period">
                第 {{ period }} 节
              </option>
            </select></label
          ><label
            ><span>结束节次</span
            ><select v-model.number="courseForm.periodEnd">
              <option v-for="period in 11" :key="period" :value="period">
                第 {{ period }} 节
              </option>
            </select></label
          >
        </div>
        <div class="form-columns">
          <label
            ><span>起始周</span
            ><input
              v-model.number="courseForm.weekStart"
              type="number"
              min="1"
              max="30" /></label
          ><label
            ><span>结束周</span
            ><input
              v-model.number="courseForm.weekEnd"
              type="number"
              min="1"
              max="30"
          /></label>
        </div>
        <div class="week-mode">
          <button
            v-for="mode in [
              { id: 'all', label: '每周' },
              { id: 'odd', label: '单周' },
              { id: 'even', label: '双周' },
            ]"
            :key="mode.id"
            type="button"
            :class="{ selected: courseForm.weekMode === mode.id }"
            @click="courseForm.weekMode = mode.id as 'all' | 'odd' | 'even'"
          >
            {{ mode.label }}
          </button>
        </div>
        <label
          ><span>免上周次</span
          ><input
            v-model="courseForm.excluded"
            placeholder="可选，例如：5、12" /></label
        ><label
          ><span>教师</span
          ><input
            v-model="courseForm.teacher"
            maxlength="100"
            placeholder="可选"
        /></label>
        <p class="editor-time">
          {{ periodTime(courseForm.periodStart, courseForm.periodEnd) }}
        </p>
        <p v-if="store.error" class="inline-error" role="alert">{{ store.error }}</p>
        <div class="sheet-actions">
          <button
            v-if="editingCourse"
            type="button"
            class="delete-action"
            :disabled="submitting"
            @click="deleteCourse"
          >
            {{ editingCourse?.id.startsWith("edit:") ? "恢复班级原课" : "移除此课" }}</button
          ><button
            class="primary-action"
            :disabled="submitting || !courseForm.name.trim()"
          >
            {{ submitting ? "保存中…" : "保存到我的课表" }}
          </button>
        </div>
      </form>
    </section>
  </div>

  <div v-if="eventDialog" class="modal-scrim" @click.self="eventDialog = false">
    <section
      class="sheet-dialog compact-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="event-title"
    >
      <header class="sheet-head">
        <div>
          <span class="eyebrow">PERSONAL SCHEDULE</span>
          <h2 id="event-title">
            {{ editingEventId ? "编辑个人日程" : "新建个人日程" }}
          </h2>
        </div>
        <button
          class="close-button"
          aria-label="关闭"
          @click="eventDialog = false"
        >
          ×
        </button>
      </header>
      <p class="sheet-explain">
        只会添加到你的时间轴，班级成员不会看到个人安排。
      </p>
      <form class="editor-form" @submit.prevent="submitEvent">
        <label
          ><span>日程内容</span
          ><input
            v-model="eventForm.title"
            required
            maxlength="180"
            placeholder="例如：实验室小组讨论"
        /></label>
        <div class="form-columns">
          <label
            ><span>开始时间</span
            ><input
              v-model="eventForm.start"
              type="datetime-local"
              required /></label
          ><label
            ><span>结束时间</span
            ><input v-model="eventForm.end" type="datetime-local"
          /></label>
        </div>
        <label
          ><span>地点</span
          ><input
            v-model="eventForm.location"
            maxlength="200"
            placeholder="可选" /></label
        ><label
          ><span>备注</span
          ><textarea
            v-model="eventForm.notes"
            maxlength="1000"
            rows="3"
            placeholder="可选"
          ></textarea>
        </label>
        <div class="sheet-actions">
          <button
            v-if="editingEventId"
            type="button"
            class="delete-action"
            @click="removeEvent"
          >
            删除日程</button
          ><button
            class="primary-action"
            :disabled="submitting || !eventForm.title.trim()"
          >
            {{ submitting ? "保存中…" : "保存日程" }}
          </button>
        </div>
      </form>
    </section>
  </div>

  <div v-if="taskDialog" class="modal-scrim" @click.self="taskDialog = false">
    <section
      class="sheet-dialog compact-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="task-title"
    >
      <header class="sheet-head">
        <div>
          <span class="eyebrow">ONE THING AT A TIME</span>
          <h2 id="task-title">新建待办</h2>
        </div>
        <button
          class="close-button"
          aria-label="关闭"
          @click="taskDialog = false"
        >
          ×
        </button>
      </header>
      <form class="editor-form" @submit.prevent="submitTask">
        <label
          ><span>要做什么？</span
          ><input
            v-model="taskTitle"
            required
            maxlength="180"
            placeholder="写下一个明确的动作" /></label
        ><label
          ><span>截止时间</span><input v-model="taskDue" type="datetime-local"
        /></label>
        <p v-if="!store.user" class="form-note">
          这是一条个人待办，登录后可保存并同步。
        </p>
        <button
          class="primary-action wide"
          :disabled="submitting || !taskTitle.trim()"
        >
          {{ store.user ? "保存待办" : "去登录后创建" }}
        </button>
      </form>
    </section>
  </div>

  <div
    v-if="handbookOpen"
    class="modal-scrim handbook-scrim"
    @click.self="handbookOpen = false"
  >
    <section
      class="handbook-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="handbook-title"
    >
      <header class="sheet-head">
        <div>
          <span class="eyebrow">STUDENT HANDBOOK</span>
          <h2 id="handbook-title">
            学生手册
            <small v-if="handbookChapters.length"
              >· {{ handbookChapters.length }} 章</small
            >
          </h2>
        </div>
        <button
          class="close-button"
          aria-label="关闭"
          @click="handbookOpen = false"
        >
          ×
        </button>
      </header>
      <div v-if="handbookError" class="handbook-state">{{ handbookError }}</div>
      <div
        v-else-if="!handbookChapters.length || handbookLoading"
        class="handbook-state"
      >
        正在载入学生手册…
      </div>
      <div v-else class="handbook-layout">
        <nav class="handbook-nav" aria-label="手册章节">
          <button
            v-for="doc in handbookChapters"
            :key="doc.chapter"
            :class="{ active: handbookSelected === doc.chapter }"
            @click="selectHandbookChapter(doc.chapter)"
          >
            {{ doc.title
            }}<small>原书 {{ doc.firstPage }}–{{ doc.lastPage }} 页</small>
          </button>
        </nav>
        <article v-if="selectedHandbook" class="handbook-content">
          <h3>{{ selectedHandbook.title }}</h3>
          <p class="handbook-summary">
            原书第 {{ selectedHandbook.firstPage }}–{{
              selectedHandbook.lastPage
            }}
            页 · {{ selectedHandbook.pages }} 页
          </p>
          <section
            v-for="page in handbookPages"
            :key="page.id"
            class="handbook-page"
          >
            <span>原书第 {{ page.printedPage }} 页</span>
            <p>{{ page.text }}</p>
          </section>
        </article>
      </div>
    </section>
  </div>
</template>
